import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DataTableSkeleton, Modal, Select, SelectItem, TextInput } from '@carbon/react';
import { CrudPage } from '@restavo/crud';
import { useOrgMembersPage } from 'src/features/orgMembers';
import { useBilling } from 'src/hooks/useBilling';
import { useInviteMember } from 'src/hooks/useInvitations';
import { showAuthPlanError } from 'src/states/planLimit';

const OrgMembers = React.memo(() => {
  const { t } = useTranslation();
  const { page, organizationId, canUpdateRow, canDeleteRow } = useOrgMembersPage();
  const inviteMember = useInviteMember(organizationId);
  const { data: billing } = useBilling();
  const adminLocked = Boolean(billing?.enabled && !billing.limits.adminRole);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'owner' | 'admin' | 'member'>('member');

  function closeInvite() {
    setInviteOpen(false);
    setInviteEmail('');
    setInviteRole('member');
  }

  // The CRUD list is keyed on the tenant params (orgSlug/restaurantId), NOT on the
  // organization id these auth endpoints need. Rendering the page before the id resolves
  // would fetch with nothing to fetch for, and React Query would cache that empty result
  // under a key that never changes again — a permanently empty table. Hold the skeleton
  // until the id is known.
  if (!organizationId) {
    return <DataTableSkeleton columnCount={5} rowCount={5} />;
  }

  return (
    <>
      <CrudPage
        {...page}
        // Nothing is created on this page: inviting produces an Invitation, which appears on
        // the Invitations page rather than in this table. So the toolbar's create button —
        // same place and size as every other page's — opens the invite dialog instead of the
        // built-in form, and the table never claims a row was added when none was.
        onCreate={() => setInviteOpen(true)}
        canUpdateRow={canUpdateRow}
        canDeleteRow={canDeleteRow}
      />
      <Modal
        open={inviteOpen}
        modalHeading={t('orgSettings.members.inviteTitle')}
        primaryButtonText={inviteMember.isPending ? t('common.loading') : t('actions.create')}
        secondaryButtonText={t('actions.cancel')}
        primaryButtonDisabled={inviteMember.isPending || !inviteEmail}
        onRequestClose={closeInvite}
        onSecondarySubmit={closeInvite}
        onRequestSubmit={async () => {
          try {
            await inviteMember.mutateAsync({ email: inviteEmail, role: inviteRole });
          } catch (error) {
            if (!showAuthPlanError(error)) throw error;
          }
          closeInvite();
        }}
      >
        <TextInput
          id="invite-email"
          type="email"
          labelText={t('common.email')}
          value={inviteEmail}
          placeholder={t('common.email')}
          onChange={(e) => setInviteEmail(e.target.value)}
        />
        <Select
          id="invite-role"
          labelText={t('common.role')}
          value={inviteRole}
          onChange={(e) => setInviteRole(e.target.value as 'owner' | 'admin' | 'member')}
        >
          <SelectItem value="owner" text={t('orgSettings.members.owner')} />
          <SelectItem
            value="admin"
            disabled={adminLocked}
            text={
              adminLocked
                ? `${t('orgSettings.members.admin')} · ${t('billing.locked')}`
                : t('orgSettings.members.admin')
            }
          />
          <SelectItem value="member" text={t('orgSettings.members.member')} />
        </Select>
      </Modal>
    </>
  );
});

export default OrgMembers;
